/**
 * Pure model of the NotificationCenter drawer (T-48, PRD §4.20). No React,
 * no i18n: unit-tested in `utils/__tests__/notificationCenterModel.test.ts`.
 *
 * Data contract:
 * - The bell (and the "לא נקראו" count) keep `GET /notifications` with no
 *   parameters: the user's UNREAD notifications, newest first, at most 50.
 * - The drawer reads a feed (B1, NL-06): `?status=all|unread&limit&before=`,
 *   newest first, paged by time ("טעינת קודמות"). `before` is inclusive of its
 *   millisecond, so a boundary row can come twice: pages are merged by id.
 * - "אישורים" narrows the loaded rows of the full feed (no server filter).
 */

/** The page size of `GET /notifications` (server `LIMIT 50`). */
export const NOTIFICATION_PAGE_LIMIT = 50;

export type DayGroup = 'today' | 'yesterday' | 'week' | 'earlier';
export const DAY_GROUP_ORDER: readonly DayGroup[] = ['today', 'yesterday', 'week', 'earlier'];

/** The fields the model needs from a notification. */
export interface NotificationLike {
  id: string;
  event_type: string;
  created_at: string;
  last_occurred_at?: string | null;
  link?: string | null;
  ticket_id?: string | null;
  read_at?: string | null;
}

/** When it last happened (a deduped notification moves up with each repeat). */
export function occurredAt(n: Pick<NotificationLike, 'created_at' | 'last_occurred_at'>): Date {
  return new Date(n.last_occurred_at || n.created_at);
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * "היום" / "אתמול" / "השבוע" (since Sunday, the Israeli week) / "קודם", in the
 * viewer's local time. A future or unparsable date counts as today.
 */
export function dayGroupOf(when: Date, now: Date): DayGroup {
  const t = when.getTime();
  if (Number.isNaN(t)) return 'today';
  const today = startOfDay(now).getTime();
  if (t >= today) return 'today';
  const yesterday = new Date(startOfDay(now)); yesterday.setDate(yesterday.getDate() - 1);
  if (t >= yesterday.getTime()) return 'yesterday';
  const week = new Date(startOfDay(now)); week.setDate(week.getDate() - now.getDay());
  if (t >= week.getTime()) return 'week';
  return 'earlier';
}

/** Rows grouped by day, groups in order, rows keep the server order inside a group; empty groups dropped. */
export function groupByDay<T extends NotificationLike>(list: readonly T[], now: Date): { group: DayGroup; items: T[] }[] {
  const buckets = new Map<DayGroup, T[]>();
  for (const n of list) {
    const g = dayGroupOf(occurredAt(n), now);
    const b = buckets.get(g);
    if (b) b.push(n); else buckets.set(g, [n]);
  }
  return DAY_GROUP_ORDER.filter((g) => buckets.has(g)).map((g) => ({ group: g, items: buckets.get(g)! }));
}

/**
 * Notifications that ask the user to approve or review something. There is no
 * server filter for this: the tab narrows the loaded rows of the full feed.
 */
export const APPROVAL_EVENT_TYPES: ReadonlySet<string> = new Set([
  'overtime_approval_requested',
  'attendance_monthly_review',
  'month_approval_required',
  'service_log_review',
  'service_log_employee_review',
  'service_log_employee_reminder',
  'service_log_manager_review',
  'service_logs_all_approved',
  'integration_submitted',
  // DQ-233: every "waiting for your approval" notice.
  'ticket_approval_requested',
  'ticket_approval_reminder',
  'extra_hours_requested',
  'lll_hours_approval_requested',
  'billing_pending_approval',
  'over_quota_hours_logged',
]);

export type NotificationTab = 'all' | 'unread' | 'approvals';

/**
 * FIX-NOTIF N-3: what "אישורים" holds for a CUSTOMER (the portal) - a request or an extra-hours
 * request waiting for their decision. (The employee's list above is unchanged.)
 */
export const PORTAL_APPROVAL_EVENT_TYPES: ReadonlySet<string> = new Set([
  'ticket_approval_requested',
  'ticket_approval_reminder',
  'overtime_approval_requested',
  'lll_hours_approval_requested',
]);

export type NotificationAudience = 'staff' | 'portal';

export function isApproval(n: Pick<NotificationLike, 'event_type'>, audience: NotificationAudience = 'staff'): boolean {
  return (audience === 'portal' ? PORTAL_APPROVAL_EVENT_TYPES : APPROVAL_EVENT_TYPES).has(n.event_type);
}

/** Not read yet (a row of the full feed may be read history). */
export function isUnread(n: Pick<NotificationLike, 'read_at'>): boolean {
  return !n.read_at;
}

export function filterByTab<T extends NotificationLike>(list: readonly T[], tab: NotificationTab, audience: NotificationAudience = 'staff'): T[] {
  if (tab === 'approvals') return list.filter((n) => isApproval(n, audience));
  if (tab === 'unread') return list.filter(isUnread);
  return [...list];
}

/* ── The drawer feed (B1): time paging ─────────────────────────────────────── */

/** Rows per page of the drawer feed (the server allows up to 100). */
export const NOTIFICATION_FEED_LIMIT = 50;

/** Which server feed a tab reads: "אישורים" narrows the full feed. */
export function feedStatusOf(tab: NotificationTab): 'all' | 'unread' {
  return tab === 'unread' ? 'unread' : 'all';
}

/** Query params of one feed page (`before` = the cursor of the previous page). */
export function feedParams(status: 'all' | 'unread', before?: string) {
  return { status, limit: NOTIFICATION_FEED_LIMIT, ...(before ? { before } : {}) };
}

/* ── Search (P-30b, D-173) ─────────────────────────────────────────────────── */

/** Typing pause before the search is sent. */
export const NOTIFICATION_SEARCH_DEBOUNCE_MS = 300;
/** The server's limit on the search text. */
export const NOTIFICATION_SEARCH_MAX = 100;

/** The text actually searched: trimmed, capped; '' = no search. */
export function normalizeSearch(text: string): string {
  return text.trim().slice(0, NOTIFICATION_SEARCH_MAX);
}

/**
 * The event types whose label AS SHOWN here contains the text (the row's
 * title is often only its type's label), sent with the search so the server
 * finds those rows too. `labels`: event type -> shown label; only plain
 * snake_case keys whose value is a string count (nested groups are skipped).
 */
export function matchingEventTypes(text: string, labels: Record<string, unknown>): string[] {
  const q = normalizeSearch(text).toLocaleLowerCase();
  if (!q) return [];
  return Object.entries(labels)
    .filter(([key, label]) => /^[a-z0-9_]+$/.test(key) && typeof label === 'string' && label.toLocaleLowerCase().includes(q))
    .map(([key]) => key)
    .sort();
}

/**
 * Query params of one page of a SEARCHED feed: the feed's own params plus the
 * text (`q`, searched on the server — older rows that were never loaded are
 * found too) and the matching types. Without text: exactly `feedParams`.
 */
export function searchFeedParams(status: 'all' | 'unread', text: string, types: string[], before?: string) {
  const q = normalizeSearch(text);
  if (!q) return feedParams(status, before);
  return { ...feedParams(status, before), q, ...(types.length > 0 ? { types: types.join(',') } : {}) };
}

export interface FeedPage<T> {
  data?: T[];
  meta?: { has_more?: boolean };
}

/**
 * The `before` of the next page, or undefined when there is none: the server
 * said `has_more: false`, the page was short, or the page brought nothing new
 * (an older server that ignores the parameters answers the same rows again).
 */
export function nextFeedCursor<T extends NotificationLike>(
  lastPage: FeedPage<T> | undefined,
  allPages: readonly (FeedPage<T> | undefined)[],
  limit = NOTIFICATION_FEED_LIMIT,
): string | undefined {
  const rows = lastPage?.data ?? [];
  if (rows.length === 0) return undefined;
  if (lastPage?.meta?.has_more === false) return undefined;
  if (lastPage?.meta?.has_more !== true && rows.length < limit) return undefined;
  const seen = new Set<string>();
  for (const p of allPages) {
    if (p === lastPage) break;
    for (const n of p?.data ?? []) seen.add(n.id);
  }
  if (seen.size > 0 && rows.every((n) => seen.has(n.id))) return undefined;
  const last = rows[rows.length - 1];
  const when = occurredAt(last);
  return Number.isNaN(when.getTime()) ? undefined : when.toISOString();
}

/** All loaded pages as one list, server order, each notification once. */
export function mergeFeedPages<T extends NotificationLike>(pages: readonly (FeedPage<T> | undefined)[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const p of pages) {
    for (const n of p?.data ?? []) {
      if (seen.has(n.id)) continue;
      seen.add(n.id);
      out.push(n);
    }
  }
  return out;
}

/** True when the bell's unread list probably has more rows than it returned. */
export function isCapped(count: number): boolean {
  return count >= NOTIFICATION_PAGE_LIMIT;
}

/**
 * Where a row goes: the server-resolved link (one resolver for the bell and
 * push), else the ticket. Null when there is nowhere better than Home.
 */
export function notificationTarget(n: Pick<NotificationLike, 'link' | 'ticket_id'>): string | null {
  const target = n.link || (n.ticket_id ? `/tickets/${n.ticket_id}` : null);
  return target && target !== '/' ? target : null;
}
